/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

const BLOCKED_INITIALS = new Set(['CU', 'KU']);

/**
 * Chooses fallback initials when the naive first+last pair is blocked: two
 * letters of the first name, else two of the last name, else one of the first.
 *
 * @param first First name segment.
 * @param last Last name segment.
 * @returns Uppercased fallback initials.
 */
function resolveBlockedInitials(first: string, last: string): string {
  if (first.length >= 2) return (first[0] + first[1]).toUpperCase();
  if (last.length >= 2) return (last[0] + last[1]).toUpperCase();
  return first[0].toUpperCase();
}

/**
 * Derives display initials from a full name.
 *
 * Single name → first letter; two+ names → first letter of first + last name.
 * If that pair is a blocked abbreviation (e.g. "CU", "KU") it cascades through
 * {@link resolveBlockedInitials}. Empty/blank input yields "?".
 *
 * @param fullName The person's full name.
 * @returns One or two uppercase initial letters, or "?" when blank.
 */
export function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';

  const first = parts[0];
  if (parts.length === 1) return first[0].toUpperCase();

  const last = parts[parts.length - 1];
  const naive = (first[0] + last[0]).toUpperCase();

  return BLOCKED_INITIALS.has(naive) ? resolveBlockedInitials(first, last) : naive;
}
